// Pick the assignment that should appear under the producer "Next up"
// label given the current wall-clock time. Pulled out as a pure
// function with an injected `now` so the producer dashboard can drive
// it from a per-minute tick.
//
// Rule: include only assignments whose filming_start is at-or-after
// `now`. Currently-running rows (already started, not yet finished)
// are deliberately excluded — "Next up" answers the question "where
// does this crew member need to be next?", and a running row is
// where they are RIGHT NOW. Including running rows reproduced the
// field complaint that long-running subject blocks like "Break
// 12:00–23:00" sat as "Next up" for the entire day until they
// ended. Pick the earliest of the eligible rows by (event_date,
// filming_start).
//
// Midnight-aware: late-night shifts (filming_start < 06:00 labeled with
// event_date=X) actually happen on calendar day X+1 per the PROduce
// schedule convention.

import { effectiveShiftStart, effectiveShiftEnd } from "@produce/shared";

export type NextAssignmentCandidate = {
  event_date: string; // ISO yyyy-mm-dd
  filming_start: string; // HH:MM or HH:MM:SS
  filming_end: string;
};

export function pickNextAssignment<T extends NextAssignmentCandidate>(
  assignments: T[],
  now: Date,
): T | null {
  const nowMs = now.getTime();

  let best: { item: T; startMs: number } | null = null;
  for (const a of assignments) {
    if (!a.event_date || !a.filming_start || !a.filming_end) continue;
    const startMs = effectiveShiftStart(a.event_date, a.filming_start).getTime();
    const endMs = effectiveShiftEnd(a.event_date, a.filming_start, a.filming_end).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) continue;

    // Skip if this assignment has already started (running OR finished).
    // start === now is treated as still-upcoming so a slot starting
    // this very minute lands as Next up rather than dropping off.
    if (startMs < nowMs) continue;

    if (best === null || startMs < best.startMs) {
      best = { item: a, startMs };
    }
  }

  return best?.item ?? null;
}
